import { OPENING_CONFIGURATION_TYPE } from 'app/typings/core/admin/experiences/enum/openingConfiguration';
import type { ExperienceResponse } from 'app/typings/core/admin/experiences/experience';
import type { PackExperienceResponse } from 'app/typings/core/admin/packs/pack';
import type { BasePriceConfiguration } from 'app/typings/core/common/openingConfiguration/priceConfigurationType';

export const DEFAULT_PAX = 2;

export type PriceRange = {
  min: number;
  max: number;
};

export type PackExperiencesPrice = PriceRange & {
  hasMissingPrice: boolean;
};

type ExperiencePricing = {
  unitPrices: number[];
  appliesDiscount: boolean;
};

const roundToCents = (amount: number) => Math.round(amount * 100) / 100;

export const formatPrice = (amount: number) => `${roundToCents(amount)}€`;

const isOriginalExperience = (experience: ExperienceResponse) =>
  !experience.hotelId;

const getPriceConfigurations = (
  experience: ExperienceResponse
): BasePriceConfiguration[] | undefined => {
  const priceConfiguration =
    experience.openingConfiguration?.priceConfiguration;

  if (!priceConfiguration) {
    return undefined;
  }

  if (priceConfiguration.type === OPENING_CONFIGURATION_TYPE.CONSTANT) {
    return [priceConfiguration.configuration];
  }

  return Object.values(priceConfiguration.days).filter(
    (configuration): configuration is BasePriceConfiguration => !!configuration
  );
};

const getExperiencePricing = (
  experience: ExperienceResponse,
  isDaytime: boolean
): ExperiencePricing | undefined => {
  const configurations = getPriceConfigurations(experience);

  if (!configurations) {
    return undefined;
  }

  const usesStaycationPrice =
    isOriginalExperience(experience) &&
    configurations.some(
      (configuration) => configuration.staycationPrice !== undefined
    );

  const unitPrices = configurations
    .map((configuration) => {
      if (usesStaycationPrice) {
        return configuration.staycationPrice;
      }

      if (isDaytime && configuration.daytimePrice !== undefined) {
        return configuration.daytimePrice;
      }

      return configuration.price;
    })
    .filter((price): price is number => price !== undefined);

  if (unitPrices.length === 0) {
    return undefined;
  }

  return { unitPrices, appliesDiscount: !usesStaycationPrice };
};

export const getAdultPriceRange = (
  experience: ExperienceResponse | undefined,
  isDaytime: boolean
): PriceRange | undefined => {
  const pricing = experience && getExperiencePricing(experience, isDaytime);

  if (!pricing) {
    return undefined;
  }

  return {
    min: Math.min(...pricing.unitPrices),
    max: Math.max(...pricing.unitPrices),
  };
};

export const getIncludedExperiencesPrice = (
  packExperiences: PackExperienceResponse[],
  experiences: ExperienceResponse[],
  isDaytime: boolean
): PackExperiencesPrice => {
  return packExperiences
    .filter((packExperience) => packExperience.isIncluded)
    .reduce<PackExperiencesPrice>(
      (total, packExperience) => {
        const experience = experiences.find(
          ({ id }) => id === packExperience.experienceId
        );
        const pricing =
          experience && getExperiencePricing(experience, isDaytime);

        if (!pricing) {
          return { ...total, hasMissingPrice: true };
        }

        const discountMultiplier = pricing.appliesDiscount
          ? 1 - (packExperience.discount ?? 0)
          : 1;
        const multiplier =
          (packExperience.quantity ?? 1) *
          (packExperience.isPerPerson ? DEFAULT_PAX : 1) *
          discountMultiplier;

        return {
          min: roundToCents(
            total.min + Math.min(...pricing.unitPrices) * multiplier
          ),
          max: roundToCents(
            total.max + Math.max(...pricing.unitPrices) * multiplier
          ),
          hasMissingPrice: total.hasMissingPrice,
        };
      },
      { min: 0, max: 0, hasMissingPrice: false }
    );
};
